import React from "react";
import { useQuery } from "@apollo/client";
import { GET_USER_PROFILE } from "src/api/graphql/graph";
import { LogoutModal } from "../../Modals/LogoutModal";
import UserAvatar from "../../SVGcomponents/Avatar.svg";
import { ReactComponent as LogoutIcon } from "../../SVGcomponents/logout-icon.svg";
import { ReactComponent as LogOutLight } from "../../SVGcomponents/logOutLight.svg";
import { ReactComponent as SettingsIconLight } from "../../SVGcomponents/settingsIconLight.svg";
import { ReactComponent as SettingsIcons } from "../../SVGcomponents/settingsIcon.svg";
import { ReactComponent as TeamIcon } from "../../SVGcomponents/team.svg";
import { ReactComponent as Wallet } from "../../SVGcomponents/wallet.svg";
import { ReactComponent as WalletLight } from "../../SVGcomponents/balanceLight.svg";
import { ReactComponent as HelpIcon } from "../../SVGcomponents/help-icon.svg";
import { ReactComponent as HelpIconLightsTheme } from "../../SVGcomponents/helpIconLight.svg";
import { ReactComponent as Advance } from "../../SVGcomponents/advance.svg";
import { ReactComponent as AdvanceLight } from "../../SVGcomponents/advanceLight.svg";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../hooks/useTheme";
import { ROLE_LABELS, useAccountAccess } from "../../../hooks/useAccountAccess";
import { setActiveAccountId } from "../../../store/accountStore";
import styles from "./styles.module.scss";
import packageJson from "../../../../package.json";

export const UserMenu: React.FC = () => {
  const { data } = useQuery(GET_USER_PROFILE);
  const { effectiveTheme } = useTheme();
  const { accounts, activeAccount, hasOtherAccounts, isOwnAccount, can } =
    useAccountAccess();

  const { name, email } = data?.checkUserProfile || {};
  const [isOpened, setIsOpened] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const version = packageJson.version;

  const navigate = useNavigate();

  const navigateToSettings = () => {
    navigate("/settings/profile");
    setIsDropdownOpen(false);
  };

  const navigateToTeam = () => {
    navigate("/settings/team");
    setIsDropdownOpen(false);
  };

  const navigateToBalance = () => {
    navigate("/balance");
    setIsDropdownOpen(false);
  };

  const navigateToHelp = () => {
    navigate("/FAQ");
    setIsDropdownOpen(false);
  };

  const navigateToAdvance = () => {
    navigate("*");
    setIsDropdownOpen(false);
  };

  const switchAccount = (id: number) => {
    setIsDropdownOpen(false);
    if (id === activeAccount?.id) return;
    // The own account is stored like any other: with nothing stored, a person
    // whose own account is empty is taken to their team (see defaultAccount).
    setActiveAccountId(id);
  };

  const toggleModal = () => {
    setIsOpened(!isOpened);
    setIsDropdownOpen(false);
  };

  return (
    <>
      <div
        className={styles.userMenuContainer}
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        <button className={styles.userAvatarButton}>
          <img
            src={UserAvatar}
            alt="User Avatar"
            className={styles.userAvatar}
          />
        </button>
        {isDropdownOpen && (
          <div className={styles.dropdownMenu}>
            <div
              className={styles.userInfo}
              onClick={() => setIsDropdownOpen(false)}
            >
              <div className={styles.userName}>{name}</div>
              <div className={styles.userEmail}>{email}</div>
            </div>
            {/* Accounts the person was invited into; the own one is first and
                clearing the choice is what returns to it. */}
            {hasOtherAccounts && (
              <>
                <div className={styles.menuDivider}></div>
                <div className={styles.accountsLabel}>Accounts</div>
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className={`${styles.menuItem} ${styles.accountItem}`}
                    onClick={() => switchAccount(account.id)}
                  >
                    <span
                      className={`${styles.menuItemTitle} ${styles.accountName}`}
                    >
                      {account.name || account.email}
                    </span>
                    {account.role && (
                      <span className={styles.accountRole}>
                        {ROLE_LABELS[account.role]}
                      </span>
                    )}
                    {account.id === activeAccount?.id && (
                      <span className={styles.accountCheck}>✓</span>
                    )}
                  </div>
                ))}
              </>
            )}
            <div className={styles.menuDivider}></div>
            <div className={styles.menuItem} onClick={navigateToSettings}>
              <div className={styles.settings}>
                <SettingsIcons className={styles.settingsIcon} />
                <SettingsIconLight className={styles.settingsIconLight} />
                <span className={styles.menuItemTitle}>Settings</span>
              </div>
            </div>
            {/* The team is the owner's and the admins' business: anybody else would only be refused by the backend. */}
            {(isOwnAccount || can("team:manage")) && (
              <div className={styles.menuItem} onClick={navigateToTeam}>
                <div className={styles.settings}>
                  <TeamIcon className={styles.teamIcon} />
                  <span className={styles.menuItemTitle}>Team</span>
                </div>
              </div>
            )}
            <div
              className={`${styles.menuItem} ${styles.mobileOnly}`}
              onClick={navigateToBalance}
            >
              <div className={styles.wallet}>
                <Wallet className={styles.walletIcon} />
                <WalletLight className={styles.walletIconLight} />
                <span className={styles.menuItemTitle}>Balance</span>
              </div>
            </div>
            <div
              className={`${styles.menuItem} ${styles.mobileOnly}`}
              onClick={navigateToHelp}
            >
              <div className={styles.helpIcon}>
                <HelpIcon className={styles.helpIconDark} />
                <HelpIconLightsTheme className={styles.helpIconLight} />
                <span className={styles.menuItemTitle}>Help</span>
              </div>
            </div>
            <div
              className={`${styles.menuItem} ${styles.mobileOnly}`}
              onClick={navigateToAdvance}
            >
              <div className={styles.advance}>
                <Advance className={styles.advanceDark} />
                <AdvanceLight className={styles.advanceLight} />
                <span className={styles.menuItemTitle}>Advanced mode</span>
              </div>
            </div>
            <div className={styles.menuItem} onClick={toggleModal}>
              <div className={styles.logout}>
                <LogoutIcon className={styles.logoutIcon} />
                <LogOutLight className={styles.logoutIconLight} />
                <span className={styles.menuItemTitle}>Log out</span>
              </div>
            </div>
            <div className={styles.policyAndVersion}>
              <div className={styles.policyLinks}>
                <a
                  className={styles.policyLink}
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy
                </a>
                <span className={styles.andSeparator}>&</span>
                <a
                  className={styles.policyLink}
                  href="/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </a>
              </div>
              <span className={styles.version}>{version}</span>
            </div>
          </div>
        )}
      </div>
      <LogoutModal isOpened={isOpened} closeModal={toggleModal} />
    </>
  );
};
